import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import emailCampaignService, { EmailCampaign } from '../../../services/emailCampaignService';

const EmailCampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadCampaigns();
  }, [filter]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await emailCampaignService.listCampaigns({
        status: filter || undefined,
        limit: 50,
      });
      setCampaigns(data.campaigns);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (campaignId: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await emailCampaignService.deleteCampaign(campaignId);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      alert('Failed to delete campaign');
    }
  };

  const handleDuplicate = async (campaignId: string) => {
    try {
      const newCampaign = await emailCampaignService.duplicateCampaign(campaignId);
      navigate(`/creator/email-campaigns/${newCampaign.id}/edit`);
    } catch (error) {
      console.error('Failed to duplicate campaign:', error);
      alert('Failed to duplicate campaign');
    }
  };

  const handleSend = async (campaignId: string) => {
    if (!confirm('Are you sure you want to send this campaign now?')) return;
    try {
      const result = await emailCampaignService.sendCampaign(campaignId);
      alert(`Campaign sent! ${result.totalSent} emails sent successfully.`);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to send campaign:', error);
      alert('Failed to send campaign');
    }
  };

  const handleCancel = async (campaignId: string) => {
    if (!confirm('Are you sure you want to cancel this scheduled campaign?')) return;
    try {
      await emailCampaignService.cancelCampaign(campaignId);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to cancel campaign:', error);
      alert('Failed to cancel campaign');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      DRAFT: 'bg-gray-100 text-gray-800',
      SCHEDULED: 'bg-blue-100 text-blue-800',
      SENDING: 'bg-yellow-100 text-yellow-800',
      SENT: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTargetTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      ALL_PATRONS: 'All Patrons',
      SPECIFIC_TIERS: 'Specific Tiers',
      ACTIVE_ONLY: 'Active Only',
      NEW_PATRONS: 'New Patrons',
      CHURNED_PATRONS: 'Churned Patrons',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="text-gray-600 mt-1">Send targeted emails to your patrons</p>
        </div>
        <button
          onClick={() => navigate('/creator/email-campaigns/new')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create Campaign
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="SCHEDULED">Scheduled</option>
          <option value="SENT">Sent</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <button
          onClick={() => navigate('/creator/email-campaigns/templates')}
          className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Templates
        </button>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
          <p className="text-gray-600 mb-4">Create your first email campaign to engage with your patrons</p>
          <button
            onClick={() => navigate('/creator/email-campaigns/new')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-sm text-gray-500">{campaign.subject}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {getTargetTypeLabel(campaign.targetType)}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(campaign.status)}
                  </td>
                  <td className="px-6 py-4">
                    {campaign.status === 'SENT' ? (
                      <div className="text-sm">
                        <div className="text-gray-900">
                          {campaign.totalSent} sent
                        </div>
                        <div className="text-gray-500">
                          {campaign.totalOpens} opens ({Math.round((campaign.totalOpens / campaign.totalSent) * 100)}%)
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {campaign.status === 'SCHEDULED'
                      ? formatDate(campaign.scheduledFor)
                      : campaign.status === 'SENT'
                      ? formatDate(campaign.sentAt)
                      : formatDate(campaign.createdAt)}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      {campaign.status === 'SENT' && (
                        <button
                          onClick={() => navigate(`/creator/email-campaigns/${campaign.id}/analytics`)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Analytics
                        </button>
                      )}
                      {(campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED') && (
                        <>
                          <button
                            onClick={() => navigate(`/creator/email-campaigns/${campaign.id}/edit`)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleSend(campaign.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Send
                          </button>
                        </>
                      )}
                      {campaign.status === 'SCHEDULED' && (
                        <button
                          onClick={() => handleCancel(campaign.id)}
                          className="text-yellow-600 hover:text-yellow-900"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        onClick={() => handleDuplicate(campaign.id)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        Duplicate
                      </button>
                      {campaign.status === 'DRAFT' && (
                        <button
                          onClick={() => handleDelete(campaign.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      {campaigns.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          Showing {campaigns.length} of {total} campaigns
        </div>
      )}
    </div>
  );
};

export default EmailCampaignsPage;
